import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ErrorBoundary } from 'react-error-boundary';

import ModeratorMenu from 'pages/Moderation/components/ModeratorMenu';
import Header from 'components/Header';
import Navigation from 'components/Navigation';
import Sidebar from 'components/Sidebar';
import { WindowSizeContext } from 'contexts/WindowSizeContext';
import ScrollRestoration from 'routes/ScrollRestoration';
import { LayoutScrollContainerProvider, ScrollPositionProvider } from 'contexts';
import ErrorBoundaryFallback from 'App/ErrorBoundaryFallback';
import { logError } from 'utils/errorHandling';

import css from './BaseLayout.module.scss';

interface Props extends React.PropsWithChildren {
    isFullHeight?: boolean;
    authorized?: boolean;
    isModerator?: boolean;
}

const BaseLayout: React.FC<Props> = ({ isFullHeight, authorized, isModerator, children }) => {
    const [sidebarActive, setSidebarActive] = useState<boolean>(false);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const { isMobile, visibleHeight } = useContext(WindowSizeContext);

    const outerContainerRef = useRef<HTMLDivElement>(null);

    const outerContainerHeight = isMobile ? visibleHeight : '100vh';

    // Google Adsense overrides our scrollable container heights with "height: auto !important", so we
    // revert it back to the original height here, otherwise scrolling will not work as expected
    // and the moderation menu will scroll together with page body.
    useEffect(() => {
        const handleMutations = (mutationsList: MutationRecord[]) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    const element = mutation.target as HTMLElement;
                    if (element.style.height === 'auto') {
                        element.style.height = outerContainerHeight.toString();
                    }
                }
            }
        };

        const observer = new MutationObserver(handleMutations);
        if (outerContainerRef.current) {
            observer.observe(outerContainerRef.current, { attributes: true, attributeFilter: ['style'] });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <ScrollPositionProvider scrollContainerRef={scrollContainerRef}>
            <LayoutScrollContainerProvider value={{ scrollContainerRef, scrollContainerId: 'scrollableDiv' }}>
                <div
                    className={css.containerWithModMenu}
                    style={{ height: outerContainerHeight }}
                    ref={outerContainerRef}
                >
                    {isModerator && <ModeratorMenu />}
                    <div className={css.containerWithMobileNav}>
                        <div
                            className={clsx(css.containerWithHeaderSidebar, isFullHeight && css.isFullHeight)}
                            ref={scrollContainerRef}
                            id="scrollableDiv"
                        >
                            <Header authorized={Boolean(authorized)} />
                            <ErrorBoundary
                                fallbackRender={ErrorBoundaryFallback}
                                onError={(error, info) => logError('BaseLayout children failed to render', error, info)}
                            >
                                {children}
                            </ErrorBoundary>
                            {authorized && (
                                <Sidebar sidebarActive={sidebarActive} setSidebarActive={setSidebarActive} />
                            )}
                        </div>
                        {authorized && <Navigation sidebarActive={sidebarActive} setSidebarActive={setSidebarActive} />}
                    </div>
                </div>
            </LayoutScrollContainerProvider>
            <ScrollRestoration containerRef={scrollContainerRef} />
        </ScrollPositionProvider>
    );
};

export default React.memo(BaseLayout);
